import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const SLOT_ORDER = { morning: 0, midday: 1, afternoon: 2, evening: 3 };
const SLOT_LABELS = { morning: '🌅 Morning', midday: '☀️ Midday', afternoon: '🌤 Afternoon', evening: '🌙 Evening' };

const CATEGORIES = [
  { value: 'turnout',      label: '🌿 Turnout' },
  { value: 'feeding',      label: '🍴 Feeding' },
  { value: 'stalls',       label: '🏠 Stalls' },
  { value: 'water',        label: '💧 Water' },
  { value: 'grooming',     label: '✂️ Grooming' },
  { value: 'tacking',      label: '🐴 Tacking' },
  { value: 'tack_cleaning',label: '🧽 Tack cleaning' },
  { value: 'horse_walker', label: '🔄 Horse walker' },
  { value: 'wrapping',     label: '🪢 Wrapping' },
  { value: 'therapy',      label: '🧊 Therapy/Treatments' },
  { value: 'medical',      label: '💊 Medical' },
  { value: 'farrier',      label: '🔨 Farrier/Hoof' },
  { value: 'night_check',  label: '🌙 Night check' },
  { value: 'inventory',    label: '📦 Inventory/Delivery' },
  { value: 'incident',     label: '⚠️ Incident' },
  { value: 'observation',  label: '👁 Observation' },
  { value: 'facility',     label: '🚿 Facility' },
  { value: 'general',      label: '📋 General' },
];

const RECURRENCES = [
  { value: 'daily',    label: 'Every day' },
  { value: 'weekdays', label: 'Weekdays only' },
  { value: 'weekends', label: 'Weekends only' },
  { value: 'custom',   label: 'Specific days' },
];

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const SLOTS = [
  { value: 'morning',   label: 'Morning' },
  { value: 'midday',    label: 'Midday' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening',   label: 'Evening' },
];

const VISIBILITIES = [
  { value: 'internal',     label: '🔒 Internal only' },
  { value: 'owner_notify', label: '📬 Notify owner' },
  { value: 'urgent',       label: '🚨 Urgent' },
];

const sh = {
  primaryBtn: {
    minHeight: 64, width: '100%', background: 'var(--cr-primary)', color: '#fff',
    border: 'none', borderRadius: 12, fontSize: 18, fontWeight: 600,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 10, cursor: 'pointer', marginBottom: 12,
  },
  secondaryBtn: {
    minHeight: 56, width: '100%', background: '#fff', color: 'var(--cr-primary)',
    border: '2px solid var(--cr-primary)', borderRadius: 12, fontSize: 18, fontWeight: 600,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 10, cursor: 'pointer', marginBottom: 12,
  },
  dangerBtn: {
    minHeight: 56, width: '100%', background: 'var(--cr-red-light)', color: 'var(--cr-red)',
    border: '2px solid var(--cr-red)', borderRadius: 12, fontSize: 18, fontWeight: 600,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 10, cursor: 'pointer', marginBottom: 12,
  },
  card: { background: '#fff', borderRadius: 14, padding: '16px', marginBottom: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  label: { fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, marginTop: 12 },
  input: { width: '100%', minHeight: 52, padding: '0 14px', fontSize: 16, borderRadius: 10, border: '1.5px solid var(--border-color)', boxSizing: 'border-box', marginBottom: 4 },
  select: { width: '100%', minHeight: 52, padding: '0 14px', fontSize: 16, borderRadius: 10, border: '1.5px solid var(--border-color)', boxSizing: 'border-box', marginBottom: 4, background: '#fff' },
};

const catLabel = v => CATEGORIES.find(c => c.value === v)?.label || v;

// ── Complete task modal ───────────────────────────────────────────────────────
function CompleteModal({ task, orgId, onDone, onClose }) {
  const [note, setNote]           = useState('');
  const [visibility, setVis]      = useState(task.default_visibility || 'internal');
  const [flagged, setFlagged]     = useState(false);
  const [flagNote, setFlagNote]   = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  const visOrder = { internal: 0, owner_notify: 1, urgent: 2 };
  const minVis = task.default_visibility || 'internal';
  const allowedVis = VISIBILITIES.filter(v => visOrder[v.value] >= visOrder[minVis]);

  const submit = async () => {
    setSaving(true); setError('');
    try {
      await api.post(`/organizations/${orgId}/barn-tasks/${task.id}/complete`, {
        note: note || null, visibility, flagged, flag_note: flagNote || null,
      });
      onDone();
    } catch (e) { setError(e.response?.data?.message || 'Could not save'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: '20px 16px 40px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ width: 40, height: 4, background: '#ddd', borderRadius: 2, margin: '0 auto 16px' }} />
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>✅ Complete task</div>
        <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>{task.title}</div>
        {error && <div style={{ background: 'var(--cr-red-light)', color: 'var(--cr-red)', padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 14 }}>{error}</div>}

        <div style={sh.label}>Note (optional)</div>
        <textarea style={{ ...sh.input, minHeight: 80, padding: '12px 14px', resize: 'vertical' }}
          placeholder="Anything to report about this task…"
          value={note} onChange={e => setNote(e.target.value)} />

        <div style={sh.label}>Visibility</div>
        <select style={sh.select} value={visibility} onChange={e => setVis(e.target.value)}>
          {allowedVis.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
        </select>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
          {visibility === 'internal' && 'Only staff and managers will see this.'}
          {visibility === 'owner_notify' && 'Manager will review before sending to owner.'}
          {visibility === 'urgent' && 'Goes to manager and owner immediately.'}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <input type="checkbox" id="flagged" checked={flagged} onChange={e => setFlagged(e.target.checked)}
            style={{ width: 22, height: 22, cursor: 'pointer' }} />
          <label htmlFor="flagged" style={{ fontSize: 16, fontWeight: 500, cursor: 'pointer' }}>⚠️ Flag for manager attention</label>
        </div>
        {flagged && (
          <textarea style={{ ...sh.input, minHeight: 60, padding: '12px 14px', resize: 'vertical' }}
            placeholder="What needs attention?"
            value={flagNote} onChange={e => setFlagNote(e.target.value)} />
        )}

        <button style={sh.primaryBtn} onClick={submit} disabled={saving}>
          {saving ? 'Saving…' : '✅ Mark complete'}
        </button>
        <button style={sh.secondaryBtn} onClick={onClose} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
}

// ── Add/edit task form (manager) ──────────────────────────────────────────────
function TaskForm({ orgId, members, horses, initial, onSave, onCancel }) {
  const blank = {
    title: '', category: 'general', description: '',
    horse_id: '', assigned_to_membership_id: '',
    recurrence: 'daily', recurrence_days: [],
    time_slot: 'morning', due_time: '',
    default_visibility: 'internal', task_meta: {},
  };
  const [form, setForm]   = useState(initial || blank);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleDay = (day) => {
    const days = form.recurrence_days || [];
    set('recurrence_days', days.includes(day) ? days.filter(d => d !== day) : [...days, day]);
  };

  const submit = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    setSaving(true); setError('');
    try {
      await onSave({
        ...form,
        horse_id: form.horse_id || null,
        assigned_to_membership_id: form.assigned_to_membership_id || null,
        due_time: form.due_time || null,
      });
    } catch (e) { setError(e.response?.data?.message || 'Could not save'); setSaving(false); }
  };

  return (
    <div>
      {error && <div style={{ background: 'var(--cr-red-light)', color: 'var(--cr-red)', padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 14 }}>{error}</div>}

      <div style={sh.label}>Task title</div>
      <input style={sh.input} placeholder="e.g. Turn out Violet" value={form.title} onChange={e => set('title', e.target.value)} />

      <div style={sh.label}>Category</div>
      <select style={sh.select} value={form.category} onChange={e => set('category', e.target.value)}>
        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
      </select>

      <div style={sh.label}>Horse (optional)</div>
      <select style={sh.select} value={form.horse_id} onChange={e => set('horse_id', e.target.value)}>
        <option value="">All horses / barn-wide</option>
        {horses.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
      </select>

      <div style={sh.label}>Assign to (optional)</div>
      <select style={sh.select} value={form.assigned_to_membership_id} onChange={e => set('assigned_to_membership_id', e.target.value)}>
        <option value="">Anyone can complete</option>
        {members.map(m => <option key={m.id} value={m.id}>{[m.account?.first_name, m.account?.last_name].filter(Boolean).join(' ') || m.account?.email || 'Member'} ({m.role})</option>)}
      </select>

      <div style={sh.label}>Recurrence</div>
      <select style={sh.select} value={form.recurrence} onChange={e => set('recurrence', e.target.value)}>
        {RECURRENCES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
      </select>

      {form.recurrence === 'custom' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {DAYS.map((d, i) => (
            <button key={i} onClick={() => toggleDay(i)} style={{
              padding: '8px 14px', borderRadius: 8, border: '2px solid var(--cr-primary)',
              background: (form.recurrence_days || []).includes(i) ? 'var(--cr-primary)' : '#fff',
              color: (form.recurrence_days || []).includes(i) ? '#fff' : 'var(--cr-primary)',
              fontWeight: 600, cursor: 'pointer', fontSize: 14,
            }}>{d}</button>
          ))}
        </div>
      )}

      <div style={sh.label}>Time slot</div>
      <select style={sh.select} value={form.time_slot} onChange={e => set('time_slot', e.target.value)}>
        {SLOTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>

      <div style={sh.label}>Due time (optional — e.g. 07:00)</div>
      <input type="time" style={sh.input} value={form.due_time} onChange={e => set('due_time', e.target.value)} />

      <div style={sh.label}>Default visibility</div>
      <select style={sh.select} value={form.default_visibility} onChange={e => set('default_visibility', e.target.value)}>
        {VISIBILITIES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
      </select>

      <div style={sh.label}>Notes / instructions</div>
      <textarea style={{ ...sh.input, minHeight: 80, padding: '12px 14px', resize: 'vertical' }}
        placeholder="Any specific instructions for this task…"
        value={form.description} onChange={e => set('description', e.target.value)} />

      <div style={{ height: 12 }} />
      <button style={sh.primaryBtn} onClick={submit} disabled={saving}>
        {saving ? 'Saving…' : '💾 Save task'}
      </button>
      <button style={sh.secondaryBtn} onClick={onCancel} disabled={saving}>Cancel</button>
    </div>
  );
}

// ── Main TasksSection ─────────────────────────────────────────────────────────
function TasksSection({ org }) {
  const [todayData, setTodayData]   = useState(null);
  const [allTasks, setAllTasks]     = useState([]);
  const [members, setMembers]       = useState([]);
  const [horses, setHorses]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [view, setView]             = useState('today'); // today | manage
  const [completing, setCompleting] = useState(null);
  const [adding, setAdding]         = useState(false);
  const [editing, setEditing]       = useState(null);
  const [error, setError]           = useState('');
  const isManager = org.my_role === 'manager';

  const loadToday = useCallback(async () => {
    try {
      const { data } = await api.get(`/organizations/${org.id}/barn-tasks/today`);
      setTodayData(data);
    } catch { setTodayData({ date: '', tasks: [] }); }
  }, [org.id]);

  const loadAll = useCallback(async () => {
    try {
      const { data } = await api.get(`/organizations/${org.id}/barn-tasks`);
      setAllTasks(data || []);
    } catch { setAllTasks([]); }
  }, [org.id]);

  const loadMembersAndHorses = useCallback(async () => {
    try {
      const [mRes, hRes] = await Promise.all([
        api.get(`/organizations/${org.id}/members`),
        api.get('/horses'),
      ]);
      setMembers(mRes.data || []);
      setHorses(hRes.data || []);
    } catch {}
  }, [org.id]);

  useEffect(() => {
    Promise.all([loadToday(), loadMembersAndHorses()]).finally(() => setLoading(false));
  }, [loadToday, loadMembersAndHorses]);

  useEffect(() => {
    if (view === 'manage') loadAll();
  }, [view, loadAll]);

  const handleSaveTask = async (form) => {
    if (editing) {
      await api.put(`/organizations/${org.id}/barn-tasks/${editing.id}`, form);
    } else {
      await api.post(`/organizations/${org.id}/barn-tasks`, form);
    }
    setAdding(false); setEditing(null);
    await Promise.all([loadToday(), loadAll()]);
  };

  const handleArchive = async (taskId) => {
    try {
      await api.delete(`/organizations/${org.id}/barn-tasks/${taskId}`);
      await loadAll();
    } catch (e) { setError(e.response?.data?.message || 'Could not archive'); }
  };

  const fmtTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    const d = new Date(); d.setHours(+h, +m);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading tasks…</div>;

  // ── Today view ──────────────────────────────────────────────────────────────
  if (view === 'today') {
    const tasks = todayData?.tasks || [];
    const bySlot = {};
    tasks.forEach(t => {
      const slot = t.time_slot || 'morning';
      if (!bySlot[slot]) bySlot[slot] = [];
      bySlot[slot].push(t);
    });
    const slots = Object.keys(bySlot).sort((a, b) => (SLOT_ORDER[a] || 0) - (SLOT_ORDER[b] || 0));
    const done  = tasks.filter(t => t.completed).length;
    const total = tasks.length;

    return (
      <div>
        {completing && (
          <CompleteModal
            task={completing} orgId={org.id}
            onDone={async () => { setCompleting(null); await loadToday(); }}
            onClose={() => setCompleting(null)}
          />
        )}

        {/* Progress bar */}
        {total > 0 && (
          <div style={sh.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Today's progress</div>
              <div style={{ fontWeight: 700, fontSize: 15, color: done === total ? 'var(--cr-green-dark)' : 'var(--cr-primary)' }}>
                {done}/{total}
              </div>
            </div>
            <div style={{ background: 'var(--bg-tertiary)', borderRadius: 8, height: 12, overflow: 'hidden' }}>
              <div style={{ background: done === total ? 'var(--cr-green)' : 'var(--cr-primary)', height: '100%', width: `${(done/total)*100}%`, borderRadius: 8, transition: 'width 0.3s' }} />
            </div>
            {done === total && <div style={{ textAlign: 'center', color: 'var(--cr-green-dark)', fontWeight: 700, fontSize: 14, marginTop: 8 }}>🎉 All done!</div>}
          </div>
        )}

        {tasks.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>✅</div>
            <div style={{ fontSize: 15 }}>No tasks scheduled for today.</div>
            {isManager && <div style={{ fontSize: 13, marginTop: 4 }}>Add recurring tasks in the Manage tab.</div>}
          </div>
        )}

        {slots.map(slot => (
          <div key={slot}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, marginTop: 16 }}>
              {SLOT_LABELS[slot] || slot}
            </div>
            {bySlot[slot].map(task => {
              const done = task.completed;
              const comp = task.completion;
              return (
                <div key={task.id} style={{
                  ...sh.card,
                  borderLeft: `4px solid ${done ? 'var(--cr-green)' : task.due_time ? 'var(--cr-amber)' : 'var(--border-color)'}`,
                  opacity: done ? 0.75 : 1,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 16, textDecoration: done ? 'line-through' : 'none', color: done ? 'var(--text-muted)' : 'var(--text-primary)' }}>
                        {task.title}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                        {catLabel(task.category)}
                        {task.horse?.name ? ` · ${task.horse.name}` : ''}
                        {task.due_time ? ` · Due ${fmtTime(task.due_time)}` : ''}
                      </div>
                      {task.description && !done && (
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, fontStyle: 'italic' }}>{task.description}</div>
                      )}
                      {done && comp && (
                        <div style={{ fontSize: 12, color: 'var(--cr-green-dark)', marginTop: 4 }}>
                          ✓ {comp.completedBy?.first_name} {comp.completedBy?.last_name} · {new Date(comp.completed_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          {comp.note ? ` · "${comp.note}"` : ''}
                          {comp.flagged ? ' · ⚠️ Flagged' : ''}
                        </div>
                      )}
                    </div>
                    {!done && (
                      <button onClick={() => setCompleting(task)} style={{
                        minWidth: 56, minHeight: 56, background: 'var(--cr-primary)', color: '#fff',
                        border: 'none', borderRadius: 12, fontSize: 24, cursor: 'pointer', marginLeft: 12,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>✓</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {isManager && (
          <button style={{ ...sh.secondaryBtn, marginTop: 16 }} onClick={() => setView('manage')}>
            ⚙️ Manage tasks
          </button>
        )}
      </div>
    );
  }

  // ── Manage view (manager only) ──────────────────────────────────────────────
  if (view === 'manage') {
    if (adding || editing) return (
      <div>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>
          {editing ? `Edit — ${editing.title}` : 'New recurring task'}
        </div>
        {error && <div style={{ background: 'var(--cr-red-light)', color: 'var(--cr-red)', padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 14 }}>{error}</div>}
        <TaskForm
          orgId={org.id} members={members} horses={horses}
          initial={editing ? {
            title: editing.title, category: editing.category,
            description: editing.description || '',
            horse_id: editing.horse_id || '',
            assigned_to_membership_id: editing.assigned_to_membership_id || '',
            recurrence: editing.recurrence, recurrence_days: editing.recurrence_days || [],
            time_slot: editing.time_slot, due_time: editing.due_time || '',
            default_visibility: editing.default_visibility, task_meta: editing.task_meta || {},
          } : null}
          onSave={handleSaveTask}
          onCancel={() => { setAdding(false); setEditing(null); setError(''); }}
        />
      </div>
    );

    const active   = allTasks.filter(t => t.is_active);
    const archived = allTasks.filter(t => !t.is_active);

    return (
      <div>
        {error && <div style={{ background: 'var(--cr-red-light)', color: 'var(--cr-red)', padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 14 }}>{error}</div>}
        <button style={sh.secondaryBtn} onClick={() => setView('today')}>← Back to today</button>

        {active.length === 0 && (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📋</div>
            <div>No recurring tasks yet.</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Add your first task below.</div>
          </div>
        )}

        {active.map(task => (
          <div key={task.id} style={sh.card}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{task.title}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
              {catLabel(task.category)}
              {task.horse?.name ? ` · ${task.horse.name}` : ' · Barn-wide'}
              {' · '}{RECURRENCES.find(r => r.value === task.recurrence)?.label || task.recurrence}
              {' · '}{SLOTS.find(s => s.value === task.time_slot)?.label || task.time_slot}
              {task.due_time ? ` · Due ${task.due_time}` : ''}
            </div>
            {task.description && <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontStyle: 'italic', marginTop: 4 }}>{task.description}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={() => setEditing(task)} style={{
                flex: 1, minHeight: 48, background: '#fff', color: 'var(--cr-primary)',
                border: '2px solid var(--cr-primary)', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 14,
              }}>✏️ Edit</button>
              <button onClick={() => handleArchive(task.id)} style={{
                flex: 1, minHeight: 48, background: 'var(--cr-red-light)', color: 'var(--cr-red)',
                border: '2px solid var(--cr-red)', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 14,
              }}>Archive</button>
            </div>
          </div>
        ))}

        <button style={{ ...sh.primaryBtn, marginTop: 8 }} onClick={() => { setAdding(true); setError(''); }}>
          + Add recurring task
        </button>

        {archived.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8 }}>ARCHIVED</div>
            {archived.map(task => (
              <div key={task.id} style={{ ...sh.card, opacity: 0.5 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{task.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{catLabel(task.category)}</div>
                <button onClick={() => api.put(`/organizations/${org.id}/barn-tasks/${task.id}`, { is_active: true }).then(loadAll)} style={{
                  marginTop: 8, background: 'none', border: '1px solid var(--border-color)',
                  borderRadius: 8, padding: '4px 12px', fontSize: 12, cursor: 'pointer', color: 'var(--text-secondary)',
                }}>Restore</button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}

export default TasksSection;
